import { useAppContext } from '@faro/app-context';
import {
    CommandResponse,
    InclusionSection,
    PlacementPayload,
    RenamePayload,
    RevisionedCommandResponse,
    StudyEntityType,
    StudySection,
} from '@faro/study-designer-model';
import { createSectionDuplicateMutation, SectionDuplicateHook } from '../createSectionDuplicateMutation';
import { createSectionInsertMutation, SectionInsertHook } from '../createSectionInsertMutation';
import {
    useInclusionSectionDelete,
    useInclusionSectionLink,
    useInclusionSectionMove,
    useInclusionSectionRename,
    useInclusionSectionUpdate,
} from './InclusionSectionHooks';

export type InclusionSectionInsertHook = SectionInsertHook<Partial<InclusionSection>>;

/**
 * Mutation hook to insert an inclusion section.
 * @returns A function that inserts an inclusion section.
 */
export const useInclusionSectionInsertMutation: (studyId: string) => InclusionSectionInsertHook =
    createSectionInsertMutation<Partial<InclusionSection>>(
        StudyEntityType.InclusionSection,
        'inclusion-sections',
        'Failed to create inclusion section'
    );

export type InclusionSectionUpdateHook = (
    inclusionSection: InclusionSection
) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to update an inclusion section.
 * @returns A function that updates an inclusion section.
 */
export function useInclusionSectionUpdateMutation(studyId: string): InclusionSectionUpdateHook {
    const { showErrorHandler } = useAppContext();

    const updateInclusionSection = useInclusionSectionUpdate({ studyId });

    function update(inclusionSection: InclusionSection) {
        return updateInclusionSection
            .mutateAsync(inclusionSection)
            .catch(showErrorHandler('Failed to update inclusion section'));
    }

    return update;
}

export type InclusionSectionDeleteHook = (inclusionSection: {
    id: string;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to delete an inclusion section.
 * @returns A function that deletes an inclusion section.
 */
export function useInclusionSectionDeleteMutation(studyId: string): InclusionSectionDeleteHook {
    const { showErrorHandler } = useAppContext();

    const deleteInclusionSection = useInclusionSectionDelete({ studyId });

    function deleteSection(inclusionSection: { id: string }) {
        return deleteInclusionSection
            .mutateAsync({
                id: inclusionSection.id,
                studyId,
            })
            .catch(showErrorHandler('Failed to delete inclusion section'));
    }

    return deleteSection;
}

export type InclusionSectionDuplicateHook = SectionDuplicateHook;

/**
 * Mutation hook to duplicate an inclusion section.
 * @returns A function that duplicates an inclusion section.
 */
export const useInclusionSectionDuplicateMutation: (studyId: string) => InclusionSectionDuplicateHook =
    createSectionDuplicateMutation(
        StudyEntityType.InclusionSection,
        'inclusion-sections',
        'Failed to duplicate inclusion section'
    );

export type InclusionSectionMoveHook = (inclusionSection: {
    id: string;
    placement: PlacementPayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to move an inclusion section.
 * @returns A function that moves an inclusion section.
 */
export function useInclusionSectionMoveMutation(studyId: string): InclusionSectionMoveHook {
    const { showErrorHandler } = useAppContext();

    const moveInclusionSection = useInclusionSectionMove({ studyId });

    function move(inclusionSection: { id: string; placement: PlacementPayload }) {
        return moveInclusionSection
            .mutateAsync({
                ...inclusionSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to move inclusion section'));
    }

    return move;
}

export type InclusionSectionLinkHook = (inclusionSection: {
    id: string;
    linkedSection: Partial<StudySection> & Pick<StudySection, 'id' | 'studyId' | 'entityType'>;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to link an inclusion section.
 * @returns A function that links an inclusion section.
 */
export function useInclusionSectionLinkMutation(studyId: string): InclusionSectionLinkHook {
    const { showErrorHandler } = useAppContext();

    const linkInclusionSection = useInclusionSectionLink({ studyId });

    function link(inclusionSection: {
        id: string;
        linkedSection: Partial<StudySection> & Pick<StudySection, 'id' | 'studyId' | 'entityType'>;
    }) {
        return linkInclusionSection
            .mutateAsync({
                ...inclusionSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to link inclusion section'));
    }

    return link;
}

export type InclusionSectionRenameHook = (inclusionSection: {
    id: string;
    renamePayload: RenamePayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to rename an inclusion section.
 * @returns A function that renames an inclusion section.
 */
export function useInclusionSectionRenameMutation(studyId: string): InclusionSectionRenameHook {
    const { showErrorHandler } = useAppContext();

    const renameInclusionSection = useInclusionSectionRename({ studyId });

    function rename(inclusionSection: { id: string; renamePayload: RenamePayload }) {
        return renameInclusionSection
            .mutateAsync({
                ...inclusionSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to rename inclusion section'));
    }

    return rename;
}
